import { YES } from "uicore-ts"
import type { InvitedCompany, RHInvitedCompanySource } from "../SocketClientFunctions"
import type { RHCPVValue } from "../Custom components/RH/Utils_slop/RHCPVCodeFormatter"


export interface RHPartInvitationState {
    partId: string
    cpvCode: string
    companies: InvitedCompany[]
}


export class ProcurementInvitationState {

    static readonly _sourcePriority: RHInvitedCompanySource[] = [
        "statistics",
        "company-interest",
        "manually-added"
    ]


    static normalizedAdditionalSourceCPVCodes(
        baseCPVCode: RHCPVValue,
        selectedCodes: RHCPVValue[]
    ): string[] {
        const baseCPV8 = ProcurementInvitationState._normalizedCPV8(baseCPVCode)
        const codeByCPV8 = new Map<string, string>()
        for (const selectedCode of selectedCodes ?? []) {
            const cpvCode = ProcurementInvitationState._exactCPVCodeToken(selectedCode)
            const cpv8 = ProcurementInvitationState._normalizedCPV8(cpvCode)
            if (!cpvCode || !cpv8 || cpv8 === baseCPV8 || codeByCPV8.has(cpv8)) {
                continue
            }
            codeByCPV8.set(cpv8, cpvCode)
        }
        return Array.from(codeByCPV8.values()).sort()
    }


    static companiesForRestoredPart(
        companies: InvitedCompany[],
        partCount: number,
        partId: string,
        cpvCode: string
    ): InvitedCompany[] {
        // A single-part procurement has only one possible association. Do not
        // discard its invitations because a legacy snapshot used a different
        // textual CPV representation (for example CPV8 instead of CPV9).
        if (partCount === 1) {
            return [...companies]
        }

        const hasAnyPartIds = companies.some(
            company => company.partIds && company.partIds.length > 0
        )
        if (hasAnyPartIds) {
            return companies.filter(company => company.partIds?.includes(partId))
        }

        const hasAnyCPVCodes = companies.some(
            company => company.cpvCodes && company.cpvCodes.length > 0
        )
        if (!hasAnyCPVCodes) {
            return [...companies]
        }

        const partCPV8 = ProcurementInvitationState._normalizedCPV8(cpvCode)
        return companies.filter(company => {
            if (!company.cpvCodes || company.cpvCodes.length === 0) {
                return YES
            }
            return company.cpvCodes.some(companyCPVCode =>
                ProcurementInvitationState._normalizedCPV8(companyCPVCode) === partCPV8
            )
        })
    }


    static selectedCompaniesForParts(parts: RHPartInvitationState[]): InvitedCompany[] {
        const companiesByIdentity = new Map<string, InvitedCompany>()
        for (const part of parts) {
            const partId = part.partId.trim()
            const cpvCode = ProcurementInvitationState._partCPVCodeToken(part.cpvCode)
            if (!partId) {
                continue
            }
            const cpvCodes = [cpvCode].filter(Boolean)
            for (const company of part.companies) {
                if (!company.isInvited) {
                    continue
                }
                const name = company.name.trim()
                if (!name) {
                    continue
                }

                const registryCode = company.registryCode?.trim()
                const email = company.email?.trim().toLowerCase()
                const identityKey = ProcurementInvitationState.identityKeyForCompany(company)
                const sources = ProcurementInvitationState.normalizedSourcesForCompany(company)
                const existingCompany = companiesByIdentity.get(identityKey)
                if (existingCompany) {
                    existingCompany.sources = ProcurementInvitationState._mergedSources(
                        existingCompany.sources,
                        sources
                    )
                    existingCompany.cpvCodes = ProcurementInvitationState._uniqueSortedValues([
                        ...(existingCompany.cpvCodes ?? []),
                        ...cpvCodes
                    ])
                    existingCompany.partIds = ProcurementInvitationState._uniqueSortedValues([
                        ...(existingCompany.partIds ?? []),
                        partId
                    ])
                    if (!existingCompany._id && company._id) {
                        existingCompany._id = company._id
                    }
                    if (!existingCompany.registryCode && registryCode) {
                        existingCompany.registryCode = registryCode
                    }
                    if (email && (!existingCompany.email || sources.includes("manually-added"))) {
                        existingCompany.email = email
                    }
                    continue
                }

                const snapshotCompany: InvitedCompany = {
                    name,
                    isInvited: YES,
                    sources,
                    partIds: [partId],
                    cpvCodes: [...cpvCodes]
                }
                if (company._id) {
                    snapshotCompany._id = company._id
                }
                if (registryCode) {
                    snapshotCompany.registryCode = registryCode
                }
                if (email) {
                    snapshotCompany.email = email
                }
                companiesByIdentity.set(identityKey, snapshotCompany)
            }
        }
        return Array.from(companiesByIdentity.values())
    }


    static invitedCompaniesToRetainDuringSourceReload(
        companies: InvitedCompany[]
    ): InvitedCompany[] {
        return companies
            .filter(company => company.isInvited)
            .map(company => ({
                ...company,
                sources: [...company.sources],
                partIds: [...(company.partIds ?? [])],
                cpvCodes: [...(company.cpvCodes ?? [])]
            }))
    }


    static companiesByAddingAvailableFinancialsAndTrackRecord(
        companies: InvitedCompany[],
        availableFinancialsAndTrackRecord: InvitedCompany[]
    ): InvitedCompany[] {
        const candidateByIdentity = new Map<string, InvitedCompany>()
        for (const candidate of availableFinancialsAndTrackRecord) {
            const identityKey = ProcurementInvitationState.identityKeyForCompany(candidate)
            if (!candidateByIdentity.has(identityKey)) {
                candidateByIdentity.set(identityKey, candidate)
            }
        }

        return companies.map(company => {
            const candidate = candidateByIdentity.get(
                ProcurementInvitationState.identityKeyForCompany(company)
            )
            if (!candidate) {
                return { ...company }
            }
            const companyWithAvailableMetrics = { ...company }
            if (candidate.taxDebt !== undefined) {
                companyWithAvailableMetrics.taxDebt = candidate.taxDebt
            }
            if (candidate.similarContractsCount !== undefined) {
                companyWithAvailableMetrics.similarContractsCount = candidate.similarContractsCount
            }
            if (candidate.taxableTurnoverThousands !== undefined) {
                companyWithAvailableMetrics.taxableTurnoverThousands = candidate.taxableTurnoverThousands
            }
            if (candidate.employeesCount !== undefined) {
                companyWithAvailableMetrics.employeesCount = candidate.employeesCount
            }
            return companyWithAvailableMetrics
        })
    }


    static companiesForSourceCustomization(
        companies: InvitedCompany[],
        sourceCPVCode: string,
        companiesBySourceCPVCode: ReadonlyMap<string, readonly InvitedCompany[]>,
        locallyAddedCompanyKeys: ReadonlySet<string>
    ): InvitedCompany[] {
        const sourceCPV8 = ProcurementInvitationState._normalizedCPV8(sourceCPVCode)
        if (!sourceCPV8) {
            return []
        }

        const currentCompanyByIdentity = new Map<string, InvitedCompany>()
        for (const company of companies) {
            const identityKey = ProcurementInvitationState.identityKeyForCompany(company)
            if (!currentCompanyByIdentity.has(identityKey)) {
                currentCompanyByIdentity.set(identityKey, company)
            }
        }

        const sourceCompanies: InvitedCompany[] = []
        for (const [candidateCPVCode, candidateCompanies] of companiesBySourceCPVCode) {
            if (ProcurementInvitationState._normalizedCPV8(candidateCPVCode) === sourceCPV8) {
                sourceCompanies.push(...candidateCompanies)
            }
        }

        const result: InvitedCompany[] = []
        const addedIdentityKeys = new Set<string>()
        for (const sourceCompany of sourceCompanies) {
            const identityKey =
                ProcurementInvitationState.identityKeyForCompany(sourceCompany)
            const currentCompany = currentCompanyByIdentity.get(identityKey)
            if (!currentCompany || addedIdentityKeys.has(identityKey)) {
                continue
            }

            const sourceSources =
                ProcurementInvitationState.normalizedSourcesForCompany(sourceCompany)
            const currentSources =
                ProcurementInvitationState.normalizedSourcesForCompany(currentCompany)
            const sources = ProcurementInvitationState._sourcePriority.filter(source =>
                sourceSources.includes(source) && currentSources.includes(source)
            )
            if (
                locallyAddedCompanyKeys.has(identityKey) &&
                !sources.includes("manually-added")
            ) {
                sources.push("manually-added")
            }
            if (sources.length === 0) {
                continue
            }

            result.push({
                ...sourceCompany,
                ...currentCompany,
                sources
            })
            addedIdentityKeys.add(identityKey)
        }

        for (const company of companies) {
            const identityKey = ProcurementInvitationState.identityKeyForCompany(company)
            if (
                !locallyAddedCompanyKeys.has(identityKey) ||
                addedIdentityKeys.has(identityKey)
            ) {
                continue
            }
            result.push({
                ...company,
                sources: ["manually-added"]
            })
            addedIdentityKeys.add(identityKey)
        }

        return result
    }


    static identityKeyForCompany(
        company: Pick<InvitedCompany, "name" | "registryCode" | "email">
    ): string {
        const registryCode = (company.registryCode ?? "").trim()
        if (registryCode) {
            return "registry:" + registryCode.toLowerCase()
        }

        const email = (company.email ?? "").trim().toLowerCase()
        if (email) {
            return "email:" + email
        }

        return "name:" + company.name.trim().toLowerCase()
    }


    static normalizedSourcesForCompany(
        company: Partial<InvitedCompany> & { source?: string }
    ): RHInvitedCompanySource[] {
        const sources = new Set<RHInvitedCompanySource>()
        let sourceValues: RHInvitedCompanySource[] = []
        if (Array.isArray(company.sources)) {
            sourceValues = company.sources
        }
        for (const source of [...sourceValues, company.source]) {
            if (source === "statistics") {
                sources.add("statistics")
            }
            else if (source === "company-interest" || source === "self-registered") {
                sources.add("company-interest")
            }
            else if (source === "manually-added") {
                sources.add("manually-added")
            }
        }
        if (sources.size === 0) {
            sources.add("manually-added")
        }
        return ProcurementInvitationState._sourcePriority.filter(source =>
            sources.has(source)
        )
    }


    static _mergedSources(
        firstSources: RHInvitedCompanySource[],
        secondSources: RHInvitedCompanySource[]
    ): RHInvitedCompanySource[] {
        const sources = new Set([...firstSources, ...secondSources])
        return ProcurementInvitationState._sourcePriority.filter(source =>
            sources.has(source)
        )
    }


    static _uniqueSortedValues(values: string[]): string[] {
        return Array.from(new Set(values)).sort()
    }


    static _normalizedCPV8(value: RHCPVValue): string {
        const digits = (value ?? "").toString().replace(/\D/g, "")
        if (digits.length < 8) {
            return ""
        }
        return digits.substring(0, 8)
    }


    static _exactCPVCodeToken(value: RHCPVValue): string {
        const token = (value ?? "").toString().trim().split(/\s+/)[0] ?? ""
        if (!/^\d{8}-\d$/.test(token)) {
            return ""
        }
        return token
    }


    static _partCPVCodeToken(value: RHCPVValue): string {
        const token = (value ?? "").toString().trim().split(/\s+/)[0] ?? ""
        if (!/^\d{8}(?:-\d)?$/.test(token)) {
            return ""
        }
        return token
    }
}
